import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { AssigTripsService } from 'src/app/component/assigned-trips/assigntrips-service/assigntrips-service';
import { getDistanceFromLatLon } from 'src/app/utils/utils.common';
import { PlannerHelpingService } from './planner-helping-service';

@Injectable({
  providedIn: 'root'
})
export class PlannerGroupService {

  user: any;
  preData: any;
  preFleet: any;
  selectedMapper: any;
  violation: any;
  timeCalibration: any;
  selectedDrivers: any[];
  selectedMethod: any;
  preDrivers: any;
  shiftMapper: any;

  constructor(
    private assigTripsService: AssigTripsService,
    private plannerHelpingService: PlannerHelpingService
  ) { }

  /**
   * Main entry point to plan trips with hybrid grouped (pooled) and solo (sequential) VRP engine.
   */
  async planTrips(user, preData, preFleet, violation, selectedMapper, selectedDrivers, selectedMethod, timeCalibration, manifestTrips, preDrivers, shiftMapper) {
    this.user = user;
    this.preData = preData;
    this.preFleet = preFleet;
    this.violation = violation;
    this.timeCalibration = timeCalibration;
    this.selectedMapper = selectedMapper;
    this.shiftMapper = shiftMapper;
    this.selectedDrivers = selectedDrivers;
    this.selectedMethod = selectedMethod;
    this.preDrivers = preDrivers;

    let planedTrips: any = {
      trips: [],
      nonCompatible: [],
      compatible: {},
      tripLogs: {},
      driversDistance: {}
    };

    const selectedTripSet = new Set<string>();
    if (this.preData.selectedTrips && Array.isArray(this.preData.selectedTrips)) {
      this.preData.selectedTrips.forEach(id => selectedTripSet.add(String(id)));
    }
    for (let trip of this.preData.trips) {
      if (selectedTripSet.has(String(trip._id))) {
        planedTrips.trips.push(trip);
      }
    }

    if (this.timeCalibration && this.timeCalibration !== "none") {
      if (this.timeCalibration === "pickup-wise") {
        await this.plannerHelpingService.applyPickupWiseCalibration(this.selectedMethod, planedTrips.trips, user);
      } else if (this.timeCalibration === "dropoff-wise") {
        await this.plannerHelpingService.applyDropoffWiseCalibration(this.selectedMethod, planedTrips.trips, user);
      } else {
        await this.plannerHelpingService.applyMemberWiseHybridCalibration(this.selectedMethod, planedTrips.trips, user);
      }
    }

    this.selectedDrivers.sort((a, b) =>
      (a.displayName || '').localeCompare(b.displayName || '')
    );

    planedTrips.trips.sort((a, b) => {
      const aTime = a.scheduleTime ? new Date(a.scheduleTime).getTime() : 0;
      const bTime = b.scheduleTime ? new Date(b.scheduleTime).getTime() : 0;
      return aTime - bTime;
    });

    // Populate pre-manifest trips if any
    if (manifestTrips && manifestTrips.length) {
      for (let item of manifestTrips) {
        if (this.selectedDrivers.some(driver => driver._id === item.driver)) {
          if (item.driver) {
            if (!planedTrips.compatible[item.driver]) {
              planedTrips.compatible[item.driver] = {
                driver: preDrivers[item.driver],
                trips: []
              };
            }
            if (item) {
              item.preTrip = true;
              planedTrips.compatible[item.driver].trips.push(item);
            }
          }
        }
      }
      // Initialize executed stops for pre-manifest assignments
      for (const driverId in planedTrips.compatible) {
        const driverData = planedTrips.compatible[driverId];
        if (driverData.trips && driverData.trips.length > 0 && !driverData.executedStops) {
          await this.initializeExecutedStopsForTrips(driverData.driver, driverData);
        }
      }
    }

    await this.assignTripsToDrivers(planedTrips, this.selectedDrivers);
    await this.finalizeInterleavedSequences(planedTrips);

    // reset trips array after planning
    planedTrips.trips = [];

    // Final Output Logs
    for (const driverId in planedTrips.compatible) {
      const data = planedTrips.compatible[driverId];
      if (data.driver) {
        console.log(`Driver: ${data.driver.displayName}`);
        console.log(`Trips IDs: ${data.trips.map(t => t.tripId).join(', ')}`);
        if (data.stops) {
          console.log(`Stops:`, data.stops.map(s => `${s.priority}. [${s.stopType}] Patient: ${s.personName || 'Unknown'} (${s.tripId}) ETA: ${s.estimatedArrivalTime}`));
        }
      }
    }

    return planedTrips;
  }

  /**
   * Initializes executed stops and baseline metrics for pre-assigned manifest trips.
   */
  async initializeExecutedStopsForTrips(driver: any, driverData: any) {
    let candidateStops = [];
    driverData.trips.forEach(trip => {
      const pair = this.buildStopsForTrip(trip);
      candidateStops.push(pair[0], pair[1]);
    });

    candidateStops.sort((a, b) => a.time - b.time);
    const sim = await this.simulateRouteFeasibility(driver, candidateStops, null, false);
    if (sim.isFeasible) {
      driverData.executedStops = sim.stops;
      this.updateTripsOrderFromStops(driverData);
    }
  }

  /**
   * Formats executed stops into user-facing stops array and attaches pickup/dropoff orders.
   */
  async finalizeInterleavedSequences(planedTrips) {
    for (const driverId in planedTrips.compatible) {
      const driverData = planedTrips.compatible[driverId];
      if (driverData.executedStops) {
        let currentLoad = 0;
        driverData.stops = driverData.executedStops.map((stop, index) => {
          if (stop.type === 'P') currentLoad++;
          const stopObj = {
            ...stop,
            priority: index + 1,
            latitude: stop.lat,
            longitude: stop.lon,
            address: stop.address || '',
            contactNumber: stop.contactNumber || '',
            passengersOnBoard: currentLoad,
            personName: stop.priorityClient ? stop.priorityClient.displayName : '',
            sortType: stop.type === 'P' ? 'pickup' : 'dropoff',
            stopType: stop.type === 'P' ? 'pickup' : 'dropoff',
            scheduledTime: stop.scheduleTime,
            appointmentTime: stop.appointmentTime,
            estimatedArrivalTime: stop.estimatedArrivalTime,
            isGrouped: stop.isGrouped,
            tripId: stop.tripId,
            _id: stop._id
          };
          if (stop.type === 'D') currentLoad = Math.max(0, currentLoad - 1);
          return stopObj;
        });

        // Determine which trips overlap with other trips in the stop sequence (pooled)
        this.detectGroupedTrips(driverData);

        driverData.trips.forEach(trip => {
          const pickupStop = driverData.stops.find(stop => stop._id === trip._id && stop.stopType === 'pickup');
          const dropoffStop = driverData.stops.find(stop => stop._id === trip._id && stop.stopType === 'dropoff');
          trip.pickupOrder = pickupStop ? pickupStop.priority : null;
          trip.dropOffOrder = dropoffStop ? dropoffStop.priority : null;
          trip.priority = trip.pickupOrder;
          trip.routeOrderLabel = (trip.pickupOrder && trip.dropOffOrder)
            ? `PU #${trip.pickupOrder} → DO #${trip.dropOffOrder}`
            : '';
        });

        this.validateAndLogAppointmentTimes(driverData, driverId);
      }
    }
  }

  /**
   * Checks which trips in the executed route had overlapping passenger occupancy (pooled).
   */
  private detectGroupedTrips(driverData: any) {
    const stops = driverData.stops || [];
    const activeTrips = new Set<string>();
    const groupedTripIds = new Set<string>();

    stops.forEach(stop => {
      if (stop.stopType === 'pickup') {
        if (activeTrips.size > 0) {
          groupedTripIds.add(stop._id);
          activeTrips.forEach(id => groupedTripIds.add(id));
        }
        activeTrips.add(stop._id);
      } else {
        activeTrips.delete(stop._id);
      }
    });

    driverData.trips.forEach(trip => {
      trip.isGrouped = groupedTripIds.has(trip._id);
    });

    stops.forEach(stop => {
      stop.isGrouped = groupedTripIds.has(stop._id);
    });
  }

  /**
   * Validates appointment time compliance and logs audit metrics.
   */
  private validateAndLogAppointmentTimes(driverData, driverId) {
    try {
      const appointmentBuffer = 5;
      const appointmentMetrics = {
        total: 0,
        onTime: 0,
        late: 0,
        tooEarly: 0,
        warnings: []
      };

      driverData.stops.forEach((stop) => {
        if (stop.stopType === 'dropoff') {
          appointmentMetrics.total++;

          const appointmentTime = moment(stop.appointmentTime).tz(this.user.timeZone);
          const estimatedArrival = moment(stop.estimatedArrivalTime).tz(this.user.timeZone);

          const minutesEarly = appointmentTime.diff(estimatedArrival, 'minutes');
          const minutesLate = estimatedArrival.diff(appointmentTime, 'minutes');

          if (minutesEarly >= 0 && minutesEarly <= appointmentBuffer) {
            appointmentMetrics.onTime++;
            console.log(
              `✓ ON-TIME: ${stop.personName} - Appointment ${appointmentTime.format('HH:mm')}, ` +
              `Arriving ${minutesEarly}min early (within buffer)`
            );
          } else if (minutesEarly > appointmentBuffer) {
            appointmentMetrics.tooEarly++;
            console.warn(
              `⚠ TOO EARLY: ${stop.personName} - Appointment ${appointmentTime.format('HH:mm')}, ` +
              `Arriving ${minutesEarly}min early`
            );
          } else if (minutesLate > 0) {
            appointmentMetrics.late++;
            console.error(
              `✗ LATE: ${stop.personName} - Appointment ${appointmentTime.format('HH:mm')}, ` +
              `Arriving ${minutesLate}min LATE`
            );
            appointmentMetrics.warnings.push({
              person: stop.personName,
              type: 'late',
              minutes: minutesLate,
              appointmentTime: appointmentTime.toISOString(),
              estimatedArrival: estimatedArrival.toISOString()
            });
          }
        }
      });

      const onTimePercentage = appointmentMetrics.total > 0
        ? ((appointmentMetrics.onTime / appointmentMetrics.total) * 100).toFixed(1)
        : 0;

      console.log(
        `\n📊 APPOINTMENT TIME SUMMARY - Driver: ${driverData.driver ? driverData.driver.displayName : driverId}\n` +
        `  Total Appointments: ${appointmentMetrics.total}\n` +
        `  ✓ On-Time: ${appointmentMetrics.onTime} (${onTimePercentage}%)\n` +
        `  ⚠ Too Early: ${appointmentMetrics.tooEarly}\n` +
        `  ✗ Late: ${appointmentMetrics.late}\n`
      );

    } catch (error) {
      console.error('Error validating appointment times:', error);
    }
  }

  /**
   * Main driver assignment loop using the hybrid VRP search.
   */
  async assignTripsToDrivers(planedTrips, drivers) {
    const clientDriverMap: any = {};

    for (let trip of planedTrips.trips) {
      const clientId = trip.priorityClient && trip.priorityClient.displayName
        ? trip.priorityClient.displayName.toLowerCase()
        : (trip.client ? String(trip.client).toLowerCase() : '');

      let candidateDrivers = [];
      let allDriversNearestTo: any = {};
      let isSameClient = false;

      if (this.violation && this.violation.prefferedToSameDriver && clientId && clientDriverMap[clientId]) {
        isSameClient = true;
        const driver = drivers.find((d) => d._id === clientDriverMap[clientId]);
        if (driver && this.isTripEligibleForDriver(planedTrips, trip, driver)) {
          candidateDrivers.push(driver);
        }
      } else {
        candidateDrivers = drivers.filter((driver) =>
          this.isTripEligibleForDriver(planedTrips, trip, driver)
        );
      }

      if (!allDriversNearestTo[trip._id]) {
        allDriversNearestTo[trip._id] = [];
      }

      let feasibleDrivers = await this.checkDriverRoutes(planedTrips, candidateDrivers, trip, allDriversNearestTo);

      if (feasibleDrivers.length === 0 && isSameClient) {
        console.warn(`Trip ${trip.tripId} (${clientId}) could not fit with preferred driver. Checking all eligible drivers.`);
        isSameClient = false;
        candidateDrivers = drivers.filter((driver) =>
          this.isTripEligibleForDriver(planedTrips, trip, driver)
        );
        feasibleDrivers = await this.checkDriverRoutes(planedTrips, candidateDrivers, trip, allDriversNearestTo);
      }

      if (feasibleDrivers.length > 0) {
        const chosen = await this.chooseDriverForTrip(
          clientDriverMap[clientId],
          feasibleDrivers,
          allDriversNearestTo,
          trip,
          planedTrips.compatible
        );

        if (chosen && chosen.driver) {
          const driverId = chosen.driver._id;
          if (!planedTrips.compatible[driverId]) {
            planedTrips.compatible[driverId] = {
              driver: chosen.driver,
              trips: [],
              executedStops: []
            };
          }

          const driverAssignment = planedTrips.compatible[driverId];
          driverAssignment.trips.push(trip);
          driverAssignment.executedStops = chosen.stops;

          // Reorder driverAssignment.trips according to the pickup sequence in executedStops
          this.updateTripsOrderFromStops(driverAssignment);

          if (!planedTrips.driversDistance[driverId]) planedTrips.driversDistance[driverId] = 0;
          planedTrips.driversDistance[driverId] = chosen.totalDistance || (planedTrips.driversDistance[driverId] + 5);

          if (clientId) {
            clientDriverMap[clientId] = driverId;
          }
        } else {
          planedTrips.nonCompatible.push(trip);
        }
      } else {
        planedTrips.nonCompatible.push(trip);
        console.warn(`Trip ${trip.tripId} (${trip.priorityClient ? trip.priorityClient.displayName : 'Unknown'}) could not be scheduled.`);
      }
    }

    return planedTrips;
  }

  /**
   * Sorts driverAssignment.trips according to the chronological order of their pickup stops.
   */
  private updateTripsOrderFromStops(driverAssignment: any) {
    if (!driverAssignment.executedStops || !driverAssignment.trips) return;
    const pickupOrderMap: any = {};
    driverAssignment.executedStops.forEach((stop, idx) => {
      if (stop.type === 'P' && pickupOrderMap[stop._id] === undefined) {
        pickupOrderMap[stop._id] = idx;
      }
    });

    driverAssignment.trips.sort((a, b) => {
      const orderA = pickupOrderMap[a._id] !== undefined ? pickupOrderMap[a._id] : 9999;
      const orderB = pickupOrderMap[b._id] !== undefined ? pickupOrderMap[b._id] : 9999;
      return orderA - orderB;
    });
  }

  /**
   * Checks static driver eligibility (shift, funding source, service area, space type, gender).
   */
  isTripEligibleForDriver(planedTrips, selectedTrip, driver) {
    let isCompatible = false;
    let logs: any = {};

    const isFundingSourceWiseExist = this.fundingSourcesWiseTrip(selectedTrip, driver);
    logs.isFundingSourceWiseExist = isFundingSourceWiseExist;

    if (isFundingSourceWiseExist) {
      const isDriverShiftWiseExist = this.checkDriverShift(selectedTrip, driver);
      logs.isDriverShiftWiseExist = isDriverShiftWiseExist;

      if (isDriverShiftWiseExist) {
        const isServiceAreasWiseExist = this.serviceAreasWiseTrip(selectedTrip, driver);
        logs.isServiceAreasWiseExist = isServiceAreasWiseExist;

        if (isServiceAreasWiseExist) {
          const isSpaceTypeWiseExist = this.spaceTypeWiseTrip(selectedTrip, driver);
          logs.isSpaceTypeWiseExist = isSpaceTypeWiseExist;

          if (isSpaceTypeWiseExist) {
            const isGenderWiseExist = this.genderWiseTrip(selectedTrip, driver);
            logs.isGenderWiseExist = isGenderWiseExist;

            if (isGenderWiseExist) {
              if (!planedTrips.driversDistance[driver._id]) planedTrips.driversDistance[driver._id] = 0;
              const isMaxFactor = this.checkTripMaxFactor(planedTrips, driver, planedTrips.driversDistance[driver._id]);
              logs.isMaxFactor = isMaxFactor;

              if (isMaxFactor) {
                isCompatible = true;
              }
            }
          }
        }
      }
    }

    if (!isCompatible) {
      if (planedTrips.tripLogs[driver._id]) {
        planedTrips.tripLogs[driver._id].reasons.push({ trip: selectedTrip, logs });
      } else {
        planedTrips.tripLogs[driver._id] = { driver, reasons: [{ trip: selectedTrip, logs }] };
      }
    }

    return isCompatible;
  }

  checkDriverShift(selectedTrip, driver) {
    let isExist = false;
    const start = moment(this.preData.date).tz(this.user.timeZone).startOf('day');
    const end = moment(this.preData.date).tz(this.user.timeZone).endOf('day');

    if (this.shiftMapper[driver._id] && this.shiftMapper[driver._id].shifts && this.shiftMapper[driver._id].shifts.length) {
      const checkTodayShift = this.shiftMapper[driver._id].shifts.find(item => item.day == start.day() && item.isEnabled);
      if (checkTodayShift && checkTodayShift.checkin && checkTodayShift.checkout) {
        start.set({ hour: Number(checkTodayShift.checkin.split(':')[0]), minute: Number(checkTodayShift.checkin.split(':')[1]) });
        end.set({ hour: Number(checkTodayShift.checkout.split(':')[0]), minute: Number(checkTodayShift.checkout.split(':')[1]) });

        const tripStart = moment(selectedTrip.scheduleTime).tz(this.user.timeZone);
        const tripEnd = moment(selectedTrip.appointmentTime).tz(this.user.timeZone);

        if (tripStart.isBetween(start, end, null, '[]') && tripEnd.isBetween(start, end, null, '[]')) {
          isExist = true;
        }
      }
    }
    return isExist;
  }

  checkTripMaxFactor(planedTrips, driver, totalDistance) {
    let isExecute = true;
    if (this.violation) {
      let numberOfTrips = true;
      if (this.violation.maxPerRun) {
        if (planedTrips.compatible[driver._id] && planedTrips.compatible[driver._id].trips) {
          if (planedTrips.compatible[driver._id].trips.length >= this.violation.maxPerRun) {
            numberOfTrips = false;
          }
        }
      }

      let numberOfTripsMiles = true;
      if (this.violation.maxDistancePerRun) {
        if (totalDistance >= this.violation.maxDistancePerRun) {
          numberOfTripsMiles = false;
        }
      }

      isExecute = (numberOfTrips && numberOfTripsMiles);
    }
    return isExecute;
  }

  fundingSourcesWiseTrip(selectedTrip, driver) {
    let isExist = false;
    const isfundingSource = driver.fundingSources && driver.fundingSources.some(d =>
      d.name === selectedTrip.cooperate &&
      moment(this.preData.date).isBetween(
        moment(d.validityRange[0]).tz(this.user.timeZone).subtract(1, 'day'),
        moment(d.validityRange[1]).tz(this.user.timeZone).add(1, 'day')
      )
    );

    if (selectedTrip.cooperate && isfundingSource) {
      const driverFleetId = this.selectedMapper ? this.selectedMapper.mappers[driver._id] : null;
      if (driverFleetId && this.preFleet[driverFleetId] && this.preFleet[driverFleetId].fundingSources) {
        if (this.preFleet[driverFleetId].fundingSources.includes(selectedTrip.cooperate)) {
          isExist = true;
        }
      }
    }
    return isExist;
  }

  serviceAreasWiseTrip(selectedTrip, driver) {
    let isExist = false;
    if (selectedTrip.serviceArea && this.shiftMapper[driver._id] && this.shiftMapper[driver._id].serviceAreas) {
      if (this.shiftMapper[driver._id].serviceAreas.includes(selectedTrip.serviceArea)) {
        isExist = true;
      }
    }
    return isExist;
  }

  spaceTypeWiseTrip(selectedTrip, driver) {
    let isExist = false;
    if (this.violation && !this.violation.isCapacityViolation) {
      const driverFleetId = this.selectedMapper ? this.selectedMapper.mappers[driver._id] : null;
      if (driverFleetId && selectedTrip.spaceType && this.preFleet[driverFleetId] && this.preFleet[driverFleetId].capacities) {
        isExist = this.preFleet[driverFleetId].capacities.some(capacity => capacity.spaceType === selectedTrip.spaceType);
      }
    } else {
      isExist = true;
    }
    return isExist;
  }

  genderWiseTrip(selectedTrip, driver) {
    let isExist = true;
    if (selectedTrip.priorityClient && selectedTrip.priorityClient.isPrefferedFemale) {
      isExist = driver.gender && driver.gender.toLowerCase() === 'female';
    }
    if (selectedTrip.preferredDriver) {
      isExist = driver._id === selectedTrip.preferredDriver;
    } else if (selectedTrip.priorityClient && selectedTrip.priorityClient.preferredDriver) {
      isExist = driver._id === selectedTrip.priorityClient.preferredDriver;
    }
    return isExist;
  }

  /**
   * Builds the [Pickup, Dropoff] stop pair for a given trip.
   */
  private buildStopsForTrip(trip: any): any[] {
    const contact = (trip.priorityClient && trip.priorityClient.contactNumber) ? trip.priorityClient.contactNumber : '';
    const pickupStop = {
      _id: trip._id,
      tripId: trip.tripId,
      type: 'P',
      time: moment(trip.scheduleTime).tz(this.user.timeZone).valueOf(),
      lat: trip.jobOriginLatitude,
      lon: trip.jobOriginLongitude,
      address: trip.jobOriginAddress || '',
      contactNumber: contact,
      spaceType: trip.spaceType,
      priorityClient: trip.priorityClient,
      scheduleTime: trip.scheduleTime,
      appointmentTime: trip.appointmentTime,
      pickupLat: trip.jobOriginLatitude,
      pickupLon: trip.jobOriginLongitude,
      dropoffLat: trip.jobDestinationLatitude,
      dropoffLon: trip.jobDestinationLongitude
    };

    const dropoffStop = {
      _id: trip._id,
      tripId: trip.tripId,
      type: 'D',
      time: moment(trip.appointmentTime).tz(this.user.timeZone).valueOf(),
      lat: trip.jobDestinationLatitude,
      lon: trip.jobDestinationLongitude,
      address: trip.jobDestinationAddress || '',
      contactNumber: contact,
      spaceType: trip.spaceType,
      priorityClient: trip.priorityClient,
      scheduleTime: trip.scheduleTime,
      appointmentTime: trip.appointmentTime,
      pickupLat: trip.jobOriginLatitude,
      pickupLon: trip.jobOriginLongitude,
      dropoffLat: trip.jobDestinationLatitude,
      dropoffLon: trip.jobDestinationLongitude
    };

    return [pickupStop, dropoffStop];
  }

  /**
   * Filters candidate drivers that can accommodate the trip in their route.
   */
  async checkDriverRoutes(planedTrips, candidateDrivers, trip, allDriversNearestTo) {
    const feasibleDrivers = await Promise.all(
      candidateDrivers.map(async (driver) => {
        const result = await this.canFitTripInDriverRoute(planedTrips, driver, trip, allDriversNearestTo);
        return result ? driver : null;
      })
    );
    return feasibleDrivers.filter(driver => driver !== null);
  }

  /**
   * Evaluates if a trip can be scheduled with this driver using either grouped interleaving or solo insertion.
   */
  async canFitTripInDriverRoute(planedTrips, driver, trip, allDriversNearestTo) {
    const bestOption = await this.evaluateDriverInsertions(planedTrips, driver, trip);

    if (bestOption && bestOption.isFeasible) {
      if (!allDriversNearestTo[trip._id]) {
        allDriversNearestTo[trip._id] = [];
      }
      allDriversNearestTo[trip._id].push({
        driverId: driver._id,
        option: bestOption,
        stops: bestOption.stops,
        totalDistance: bestOption.totalDistance,
        score: bestOption.score,
        isGrouped: bestOption.isGrouped
      });
      return true;
    } else {
      let logs = {
        isFundingSourceWiseExist: true,
        isDriverShiftWiseExist: true,
        isServiceAreasWiseExist: true,
        isSpaceTypeWiseExist: true,
        istimeWiseTripExist: false
      };

      if (planedTrips.tripLogs[driver._id]) {
        planedTrips.tripLogs[driver._id].reasons.push({ trip: trip, logs });
      } else {
        planedTrips.tripLogs[driver._id] = { driver, reasons: [{ trip: trip, logs }] };
      }
      return false;
    }
  }

  /**
   * Unified Dual-Path VRP Evaluator:
   * 1. Evaluates Grouped (pooled) stop interleaving where P_trip and D_trip overlap with existing passengers.
   * 2. Evaluates Sequential Solo insertions (before, between, or after) where P_trip and D_trip run consecutively.
   * Ranks all feasible insertions and returns the optimal configuration.
   */
  async evaluateDriverInsertions(planedTrips, driver, trip): Promise<any> {
    const assignedTrips = (planedTrips.compatible[driver._id] && planedTrips.compatible[driver._id].trips)
      ? planedTrips.compatible[driver._id].trips
      : [];

    const currentStops: any[] = (planedTrips.compatible[driver._id] && planedTrips.compatible[driver._id].executedStops)
      ? planedTrips.compatible[driver._id].executedStops
      : [];

    const tripStops = this.buildStopsForTrip(trip);
    const pStop = tripStops[0];
    const dStop = tripStops[1];

    // Case 1: Driver has no existing trips assigned -> First Trip Possibility
    if (assignedTrips.length === 0 || currentStops.length === 0) {
      const candidate = [pStop, dStop];
      const sim = await this.simulateRouteFeasibility(driver, candidate, trip, false);
      if (sim.isFeasible) {
        return {
          isFeasible: true,
          stops: sim.stops,
          totalDistance: sim.totalDistance,
          totalDuration: sim.totalDuration,
          score: sim.totalDistance + (0.2 * sim.totalDuration),
          isGrouped: false
        };
      }
      return { isFeasible: false, reason: sim.reason };
    }

    // Baseline route metrics before inserting new trip
    let baseDistance = 0;
    let baseDuration = 0;
    if (currentStops.length > 0) {
      const baseSim = await this.simulateRouteFeasibility(driver, currentStops, null, false);
      if (baseSim.isFeasible) {
        baseDistance = baseSim.totalDistance;
        baseDuration = baseSim.totalDuration;
      }
    }

    const feasibleOptions: any[] = [];
    const N = currentStops.length;

    // Explore insertion positions (i, j) where 0 <= i <= N and i < j <= N + 1
    // i: index where pickup is inserted; j: index where dropoff is inserted in the new array
    for (let i = 0; i <= N; i++) {
      // Temporal pruning for pickup:
      // If inserting at i, check temporal proximity to previous and next stop
      if (i > 0 && i < N) {
        const prevTime = currentStops[i - 1].time;
        const nextTime = currentStops[i].time;
        // If pickup time is way outside the [prevTime - 45m, nextTime + 45m] bracket, skip
        if (pStop.time < prevTime - (45 * 60000) || pStop.time > nextTime + (45 * 60000)) {
          continue;
        }
      }

      for (let j = i + 1; j <= N + 1; j++) {
        // Temporal pruning for dropoff:
        if (j > 1 && j <= N) {
          const prevDropTime = currentStops[j - 2].time;
          const nextDropTime = currentStops[j - 1] ? currentStops[j - 1].time : Infinity;
          if (dStop.time < prevDropTime - (45 * 60000) || (nextDropTime !== Infinity && dStop.time > nextDropTime + (45 * 60000))) {
            continue;
          }
        }

        // Construct candidate sequence:
        // Elements before i: currentStops[0 ... i-1]
        // Insert pStop
        // Elements between i and j-1: currentStops[i ... j-2]
        // Insert dStop
        // Elements from j-1 onwards: currentStops[j-1 ... N-1]
        const candidate: any[] = [];
        for (let k = 0; k < i; k++) {
          candidate.push(currentStops[k]);
        }
        candidate.push(pStop);
        for (let k = i; k < j - 1; k++) {
          candidate.push(currentStops[k]);
        }
        candidate.push(dStop);
        for (let k = j - 1; k < N; k++) {
          candidate.push(currentStops[k]);
        }

        // Determine if this insertion is Grouped (pooled) or Solo (sequential)
        // It is grouped if between pStop and dStop there are stops of other trips,
        // OR if pStop was inserted while another trip was already onboard.
        const isCandidateGrouped = (j > i + 1) || (i > 0 && i < N && this.isVehicleOccupiedAt(currentStops, i));

        // Evaluate chronological route feasibility
        const sim = await this.simulateRouteFeasibility(driver, candidate, trip, isCandidateGrouped);
        if (sim.isFeasible) {
          const addedDistance = Math.max(0, sim.totalDistance - baseDistance);
          const addedDuration = Math.max(0, sim.totalDuration - baseDuration);

          // Grouping bonus: reward vehicle consolidation by subtracting equivalent mileage cost
          const groupingBonus = isCandidateGrouped ? 15 : 0;
          const score = addedDistance + (0.2 * addedDuration) - groupingBonus;

          feasibleOptions.push({
            isFeasible: true,
            stops: sim.stops,
            totalDistance: sim.totalDistance,
            totalDuration: sim.totalDuration,
            score: score,
            isGrouped: isCandidateGrouped,
            insertIndex: i
          });
        }
      }
    }

    if (feasibleOptions.length === 0) {
      return { isFeasible: false, reason: 'No feasible stop sequence meets constraints' };
    }

    // Sort by objective score ascending (lowest added cost wins)
    feasibleOptions.sort((a, b) => a.score - b.score);
    return feasibleOptions[0];
  }

  /**
   * Checks if vehicle is carrying passengers at index stopIdx in currentStops.
   */
  private isVehicleOccupiedAt(stops: any[], stopIdx: number): boolean {
    let load = 0;
    for (let k = 0; k < stopIdx; k++) {
      if (stops[k].type === 'P') load++;
      else load--;
    }
    return load > 0;
  }

  /**
   * Chronological Route Simulation Engine:
   * Validates:
   * 1. Cumulative multi-space-type capacity at every leg.
   * 2. Shift start & checkout times (with cushion).
   * 3. On-time dropoff appointment compliance (appointmentBuffer <= 5 min).
   * 4. Passenger detour / maximum transit time: actual ride <= max(direct * 1.35 + 10, direct + 20).
   * 5. Group pickup radius (default 8 miles) when vehicle is occupied.
   * 6. Vehicle dwell/service times per spaceType.
   */
  async simulateRouteFeasibility(
    driver: any,
    stops: any[],
    newTrip: any,
    isGrouped: boolean
  ): Promise<{ isFeasible: boolean; stops?: any[]; totalDistance?: number; totalDuration?: number; isGrouped?: boolean; reason?: string }> {

    if (!stops || stops.length === 0) {
      return { isFeasible: false, reason: 'Empty stop list' };
    }

    const startOfDay = moment(this.preData.date).tz(this.user.timeZone).startOf('day');
    const todayShift = this.shiftMapper && this.shiftMapper[driver._id] && this.shiftMapper[driver._id].shifts
      ? this.shiftMapper[driver._id].shifts.find(s => s.day === startOfDay.day() && s.isEnabled)
      : null;

    if (!todayShift || !todayShift.checkin || !todayShift.checkout) {
      return { isFeasible: false, reason: 'Driver shift not found' };
    }

    let currentTime = moment(startOfDay).set({
      hour: Number(todayShift.checkin.split(':')[0]),
      minute: Number(todayShift.checkin.split(':')[1])
    });

    let checkoutTime = moment(startOfDay).set({
      hour: Number(todayShift.checkout.split(':')[0]),
      minute: Number(todayShift.checkout.split(':')[1])
    });

    let currentLat = driver.startingPointAddress ? driver.startingPointAddress.latitude : null;
    let currentLon = driver.startingPointAddress ? driver.startingPointAddress.longitude : null;

    if (currentLat === null || currentLon === null) {
      return { isFeasible: false, reason: 'Driver has no starting point coordinates' };
    }

    const relaxationMargin = (this.violation && this.violation.groupCushion) ? this.violation.groupCushion : 15;
    const groupRadiusLimit = (this.violation && this.violation.groupRadius) ? this.violation.groupRadius : 8;

    // Vehicle fleet capacity map
    const driverFleetId = this.selectedMapper ? this.selectedMapper.mappers[driver._id] : null;
    const vehicleCapacities: any[] = (driverFleetId && this.preFleet[driverFleetId])
      ? (this.preFleet[driverFleetId].capacities || [])
      : [];

    const capMap: any = {};
    vehicleCapacities.forEach(c => {
      capMap[c.spaceType] = c.capacity;
    });

    const currentLoad: any = {};
    const pickupDepartureTimes: any = {};
    let passengersOnBoard = 0;
    let totalDistance = 0;
    let totalDuration = 0;

    const simulatedStops = [];

    for (let sIdx = 0; sIdx < stops.length; sIdx++) {
      const stop = { ...stops[sIdx] };

      // Calculate driving travel time and distance
      const travel = await this.calculateDistance(currentLat, currentLon, stop.lat, stop.lon);
      const legDist = travel && travel.distance ? travel.distance : 0;
      const legDur = travel && travel.duration ? travel.duration : 0;

      totalDistance += legDist;
      totalDuration += legDur;
      currentTime.add(legDur, 'minutes');

      // Pickup Group Radius check:
      // If vehicle already has passengers, the distance to the next grouped pickup must not exceed limit
      if (isGrouped && stop.type === 'P' && newTrip && stop._id === newTrip._id && passengersOnBoard > 0) {
        if (legDist > groupRadiusLimit) {
          if (this.violation && !this.violation.isNearbyViolation) {
            return { isFeasible: false, reason: `Exceeded group pickup radius (${legDist.toFixed(1)}mi > ${groupRadiusLimit}mi)` };
          }
        }
      }

      // Early Arrival & Earliest Window
      const stopTargetTime = moment(stop.time).tz(this.user.timeZone);
      const earliestWindow = stopTargetTime.clone().subtract(15, 'minutes');

      const idleTime = stopTargetTime.diff(currentTime, 'minutes');
      if (idleTime > 20) {
        currentTime = stopTargetTime.clone();
      } else if (currentTime.isBefore(earliestWindow)) {
        currentTime = earliestWindow.clone();
      }

      stop.estimatedArrivalTime = currentTime.toISOString();

      // Dropoff Appointment Constraint (STRICT - NO LATE ARRIVALS)
      if (stop.type === 'D') {
        const appointmentBuffer = 5; // permissible check-in buffer in minutes
        const maxAllowedArrival = stopTargetTime.clone().add(appointmentBuffer, 'minutes');

        if (currentTime.isAfter(maxAllowedArrival)) {
          return {
            isFeasible: false,
            reason: `Late dropoff for ${stop.personName || stop.tripId}: arrives ${currentTime.format('HH:mm')}, appointment was ${stopTargetTime.format('HH:mm')}`
          };
        }

        // Passenger Detour / Ride-time ratio constraint (DARP best practice)
        const pickupDepTime = pickupDepartureTimes[stop._id];
        if (pickupDepTime) {
          const actualRideMinutes = currentTime.diff(pickupDepTime, 'minutes');
          let directRide = stop._directDuration;
          if (directRide === undefined) {
            const directDist = await this.calculateDistance(stop.pickupLat, stop.pickupLon, stop.lat, stop.lon);
            directRide = directDist && directDist.duration ? directDist.duration : 0;
            stop._directDuration = directRide;
          }

          // Detour limit: direct * 1.35 + 10 mins (or at least direct + 20 mins)
          const maxAllowedRide = Math.max(Math.round(directRide * 1.35) + 10, directRide + 20);
          if (actualRideMinutes > maxAllowedRide) {
            return {
              isFeasible: false,
              reason: `Detour ride time exceeded for ${stop.tripId}: actual ${actualRideMinutes}m vs max ${maxAllowedRide}m (direct ${directRide}m)`
            };
          }
        }
      }

      // Pickup Time Window check
      if (stop.type === 'P' && this.violation && !this.violation.isTimeViolation) {
        if (currentTime.isAfter(stopTargetTime.clone().add(relaxationMargin, 'minutes'))) {
          return { isFeasible: false, reason: `Pickup arrival exceeded relaxation margin for ${stop.tripId}` };
        }
      }

      // Dynamic Capacity Check per spaceType
      if (stop.type === 'P') {
        currentLoad[stop.spaceType] = (currentLoad[stop.spaceType] || 0) + 1;
        passengersOnBoard++;
        if (this.violation && !this.violation.isCapacityViolation) {
          const cap = capMap[stop.spaceType] !== undefined ? capMap[stop.spaceType] : 1;
          if (currentLoad[stop.spaceType] > cap) {
            return {
              isFeasible: false,
              reason: `Vehicle capacity exceeded for spaceType: ${stop.spaceType} (load: ${currentLoad[stop.spaceType]}, cap: ${cap})`
            };
          }
        }
      } else {
        currentLoad[stop.spaceType] = Math.max(0, (currentLoad[stop.spaceType] || 0) - 1);
        passengersOnBoard = Math.max(0, passengersOnBoard - 1);
      }

      // Shared Location and Dwell/Service Time
      let serviceTimeMinutes = 0;
      if (currentLat === stop.lat && currentLon === stop.lon) {
        serviceTimeMinutes = 2; // shared co-passenger stop
      } else {
        const stData = this.preData.spaceTypes ? this.preData.spaceTypes[stop.spaceType] : null;
        if (stData) {
          const stValue = stData[stop.type === 'P' ? 'loadTime' : 'unloadTime'];
          if (stValue && stValue.includes(':')) {
            const parts = stValue.split(':').map(Number);
            serviceTimeMinutes = (parts[0] * 60) + parts[1];
          }
        }
      }

      currentTime.add(serviceTimeMinutes, 'minutes');

      // Driver cannot depart pickup stop before target pickup time
      if (stop.type === 'P' && currentTime.isBefore(stopTargetTime)) {
        currentTime = stopTargetTime.clone();
      }

      if (stop.type === 'P') {
        pickupDepartureTimes[stop._id] = currentTime.clone();
      }

      currentLat = stop.lat;
      currentLon = stop.lon;
      simulatedStops.push(stop);
    }

    // Driver Shift Checkout limit check
    if (currentTime.isAfter(checkoutTime.clone().add(relaxationMargin, 'minutes'))) {
      return { isFeasible: false, reason: `Route exceeds driver shift checkout time (${currentTime.format('HH:mm')} > ${checkoutTime.format('HH:mm')})` };
    }

    return {
      isFeasible: true,
      stops: simulatedStops.map((s, idx) => ({ ...s, priority: idx + 1 })),
      totalDistance: Math.round(totalDistance * 10) / 10,
      totalDuration: Math.round(totalDuration),
      isGrouped: isGrouped
    };
  }

  /**
   * Chooses the optimal driver among feasible options based on minimal added objective cost.
   */
  async chooseDriverForTrip(clientDriverId, feasibleDrivers, allDriversNearestTo, selectedTrip, compatible) {
    let bestChoice: any = null;
    let minScore = Infinity;

    for (let driver of feasibleDrivers) {
      const candidates = allDriversNearestTo[selectedTrip._id] || [];
      const driverOption = candidates.find(item => item.driverId === driver._id);
      if (!driverOption) continue;

      // Honor same-driver preference for the same client if configured
      if (this.violation && this.violation.prefferedToSameDriver && clientDriverId) {
        if (clientDriverId === driver._id) {
          bestChoice = {
            driver,
            stops: driverOption.stops,
            totalDistance: driverOption.totalDistance,
            isGrouped: driverOption.isGrouped
          };
          break;
        }
      }

      const score = driverOption.score !== undefined ? driverOption.score : driverOption.totalDistance;
      if (score < minScore) {
        minScore = score;
        bestChoice = {
          driver,
          stops: driverOption.stops,
          totalDistance: driverOption.totalDistance,
          isGrouped: driverOption.isGrouped
        };
      }
    }

    return bestChoice;
  }

  async calculateDistance(lat1, lon1, lat2, lon2) {
    return await this.assigTripsService.getDistance(lat1, lon1, lat2, lon2, this.selectedMethod);
  }
}
